/**
 * BackIdeaDialog (T35.7.2 Part A → .2 checkout wiring) — the post-upvote backing
 * dialog.
 *
 * Owned by the Feature Ideas module. Composes `SubscriptionPicker` (the agnostic
 * amount picker, owned by the subscriptions module) with the idea-backing-specific
 * framing: the "Back this idea" header + the idea title + the budget copy +
 * bracketed-influence note.
 *
 * Flow (T35.7.2.2): record (`/api/subscriptions/:id/subscribe`) → `createSession`
 * → `<PaymentHandoff>`. The stub processor has no card step (the `/session`
 * finalizes the row synchronously) → immediate success. Stripe mounts the Payment
 * Element; the SUCCESS message fires the moment the charge goes through
 * (`onResult(true)`) — NOT after a webhook. The success copy is the SAME universal
 * message as a donation (no backing-specific thank-you).
 *
 * Phase machine: `pick → pay → (success | failed)`. `failed` shows the universal
 * "didn't go through — you weren't charged" message with `Try again` → `pick`.
 *
 * No shared feedback-corpus dependency.
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Modal } from '../../components/Modal';
import { SubscriptionPicker, subscriptionSuffix } from '../subscriptions/SubscriptionPicker';
import { PaymentHandoff } from '../subscriptions/paymentRenderAdapters';
import { PaymentSuccess, PaymentFailure } from '../subscriptions/PaymentOutcome';
import {
  backIdea,
  createSession,
  abandonSubscription,
  type RecurringSchedule,
  type SessionDescriptor,
} from '../subscriptions/api';

const targetStyle: CSSProperties = {
  margin: '0 0 var(--space-2)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const budgetStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  margin: '0 0 var(--space-3)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const footStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 1.45,
};

type Phase = 'pick' | 'pay' | 'success' | 'failed';

export function BackIdeaDialog({
  open,
  ideaId,
  ideaTitle,
  busy,
  setBusy,
  onClose,
  onConfirmed,
}: {
  open: boolean;
  ideaId: string;
  ideaTitle: string;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onClose: () => void;
  /** Called with the confirmed dollar amount + the recurring suffix (e.g. "/month"). */
  onConfirmed: (amount: number, suffix: string) => void;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('pick');
  const [descriptor, setDescriptor] = useState<SessionDescriptor | null>(null);
  const [failReason, setFailReason] = useState<string | undefined>(undefined);
  // The intent row created this attempt, so the failure/cancel path can abandon
  // it (BUG34) — clearing the never-charged row off Settings → Subscriptions.
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

  // Reset the flow each time the dialog opens.
  useEffect(() => {
    if (open) {
      setPhase('pick');
      setDescriptor(null);
      setFailReason(undefined);
      setSubscriptionId(null);
    }
  }, [open]);

  async function confirmBacking(amountCents: number, recurring: RecurringSchedule): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailReason(undefined);
    let createdId: string | null = null;
    try {
      const sub = await backIdea(ideaId, amountCents, recurring);
      createdId = sub.id;
      setSubscriptionId(sub.id);
      const session = await createSession(sub.id);
      setDescriptor(session);
      if (session.primary.processor === 'stub') {
        // The stub has no card step — `/session` already finalized the row.
        onConfirmed(Math.round(amountCents / 100), subscriptionSuffix(recurring));
        setPhase('success');
        return;
      }
      // A real processor (Stripe) — mount the Payment Element to collect the card.
      setPhase('pay');
    } catch {
      // record OR createSession threw — the payment never started, so the user
      // was NOT charged. Abandon the intent row if one was created (BUG34) so it
      // never lingers in Settings, then show the failure message.
      if (createdId) void abandonSubscription(createdId);
      setPhase('failed');
      setFailReason(undefined);
    } finally {
      setBusy(false);
    }
  }

  /** The payment surface reported a result. `true` = charged → universal success. */
  function onPaymentResult(ok: boolean, reason?: string): void {
    if (ok) {
      onConfirmed(0, '');
      setPhase('success');
    } else {
      // Declined / errored at the processor — not charged. Abandon the intent row
      // (BUG34) so it leaves Settings immediately, then carry the reason.
      if (subscriptionId) void abandonSubscription(subscriptionId);
      setFailReason(reason);
      setPhase('failed');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Back this idea"
      ariaLabel="Back this idea"
      variant="sheet"
    >
      <div data-testid="back-idea-dialog">
        <p style={targetStyle} data-testid="back-idea-target">
          {ideaTitle}
        </p>

        {phase === 'success' ? (
          <PaymentSuccess testIdPrefix="back-idea" onDone={onClose} />
        ) : phase === 'failed' ? (
          <PaymentFailure
            testIdPrefix="back-idea"
            reason={failReason}
            onRetry={() => {
              setFailReason(undefined);
              setPhase('pick');
            }}
          />
        ) : phase === 'pay' && descriptor ? (
          <PaymentHandoff descriptor={descriptor} onResult={onPaymentResult} />
        ) : (
          <>
            <p style={budgetStyle}>
              <span aria-hidden="true">ⓘ</span>
              <span data-testid="back-idea-budget">
                Back any amount you like. We bracket how much one household&apos;s backing sways the
                ranking — so it signals what matters, it doesn&apos;t buy the roadmap.
              </span>
            </p>

            <SubscriptionPicker
              open={open}
              confirmLabel="Back it"
              busy={busy}
              onClose={onClose}
              onConfirm={confirmBacking}
            />

            <p style={footStyle}>
              <span aria-hidden="true">✓ </span>
              It&apos;s not a pre-order — we decide what gets built. Your backing is private to your
              household.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
